import { SystemClock } from '@puzzlemania/core';
import { EconomyService } from '@puzzlemania/economy';
import { RemoteConfigService } from '@puzzlemania/remote-config';
const LS_NS = 'pm_save_';
/**
 * Local-first per-game save: reads/writes localStorage synchronously (unchanged
 * behavior for every existing game), and additionally write-throughs to a real
 * IDataProvider when the composition root supplies one — `_remote` stays undefined
 * for every test/default construction, so nothing regresses there.
 *
 * WP-50.6 — this constructor is the ONLY place a persisted save re-enters the
 * app (`HomeScreen` reads the same keys, but only to ask whether one exists), so
 * it is where validation belongs. Without a validator it still returns any
 * syntactically valid JSON verbatim, which is what it always did.
 */
export class LocalSaveModule {
    constructor(_key, options) {
        this._key = _key;
        this._data = null;
        this.version = 1;
        this._remote =
            options?.provider && options.uid !== undefined
                ? { provider: options.provider, uid: options.uid }
                : undefined;
        try {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_NS + _key) : null;
            if (raw) {
                const parsed = JSON.parse(raw);
                this._data = options?.validate ? options.validate(parsed) : parsed;
            }
        }
        catch {
            /* ignore — localStorage may be unavailable or corrupt */
        }
    }
    getKey() {
        return this._key;
    }
    save(data) {
        this._data = data;
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(LS_NS + this._key, JSON.stringify(data));
            }
        }
        catch {
            /* quota exceeded or private browsing — fail silently */
        }
        if (this._remote) {
            void this._remote.provider.set(`saves/${this._remote.uid}/modules/${this._key}`, data);
        }
    }
    load() {
        return this._data;
    }
    migrate(_old, _fromVersion) {
        return _old;
    }
}
/**
 * Module 124 — a per-game save whose ONLY store is the host's `IDataProvider`,
 * for hosts whose storage is the player's save of record (Playgama Bridge:
 * checklist item 4, "save and load progress only through Storage, never
 * through `localStorage` directly").
 *
 * The provider is async and `ISaveModule.load()` is not, so the composition
 * root must `await hydrate()` before the game initialises, the same moment the
 * wallet is loaded. A module that is never hydrated reads as "no save".
 *
 * ## Migration from `LocalSaveModule` (D3)
 *
 * Every write since Module 59 went to the provider too, so the provider copy is
 * normally there and wins. Only when it is missing is the legacy
 * `localStorage` copy used, validated, and written up. The legacy key is
 * removed only once the provider copy has been SEEN, never on the boot that
 * migrates. A provider whose write silently failed (the mock platform rejects
 * all storage) must not cost the player the only copy they have. After that it
 * is gone for good, so a deleted provider save (the QA tool's reset) cannot be
 * resurrected from it.
 */
export class ProviderSaveModule {
    constructor(_key, _opts) {
        this._key = _key;
        this._opts = _opts;
        this._data = null;
        this.version = 1;
    }
    getKey() {
        return this._key;
    }
    /** Reads the provider copy. Must be called, and awaited, before the game reads `load()`. */
    async hydrate() {
        const raw = await this._opts.provider.get(this._remoteKey());
        const stored = raw === null ? null : this._validate(raw);
        if (stored !== null) {
            this._data = stored;
            this._legacy('remove');
            return;
        }
        const legacy = this._legacy('read');
        if (legacy !== null) {
            this._data = legacy;
            void this._opts.provider.set(this._remoteKey(), legacy);
        }
    }
    save(data) {
        this._data = data;
        void this._opts.provider.set(this._remoteKey(), data);
    }
    load() {
        return this._data;
    }
    migrate(_old, _fromVersion) {
        return _old;
    }
    _remoteKey() {
        return `saves/${this._opts.uid}/modules/${this._key}`;
    }
    _validate(raw) {
        return this._opts.validate ? this._opts.validate(raw) : raw;
    }
    _legacy(op) {
        try {
            const store = this._opts.legacyStorage !== undefined
                ? this._opts.legacyStorage
                : typeof localStorage !== 'undefined'
                    ? localStorage
                    : null;
            if (!store)
                return null;
            if (op === 'remove') {
                store.removeItem(LS_NS + this._key);
                return null;
            }
            const raw = store.getItem(LS_NS + this._key);
            return raw ? this._validate(JSON.parse(raw)) : null;
        }
        catch {
            return null; // storage unavailable or the legacy copy is corrupt — treat as absent
        }
    }
}
class NullAnalyticsProvider {
    logEvent(_name, _params) { }
    setUserProperty(_key, _value) { }
}
/**
 * Module 41 (WP-41.6) — what a game may do when nobody says otherwise: exactly
 * what the PuzzleMania app has always allowed.
 *
 * This default is the whole reason the change is safe. Every existing caller —
 * the main app, Android, every test, every standalone construction — keeps the
 * behaviour it had before `host` existed, and only a composition root that
 * *declares* a restriction gets one. `offerRewardedAd` is absent rather than a
 * stub resolving `false`: presence is how a game decides whether to render an ad
 * affordance at all, so a stub would produce a button that always disappoints.
 */
export const DEFAULT_GAME_HOST = Object.freeze({
    canExit: true,
    canShop: true,
    showGems: true,
    canOpenLegal: true,
    canRestorePurchases: true,
    hasIap: true,
});
export class GameContextBuilder {
    constructor(opts = {}) {
        this._analyticsProvider = opts.analyticsProvider ?? new NullAnalyticsProvider();
        this._economy = opts.economy ?? new EconomyService();
        this._remoteConfig = opts.remoteConfig ?? new RemoteConfigService();
        this._saveModuleFactory =
            opts.saveModuleFactory ?? ((key) => new LocalSaveModule(key));
        this._audio = opts.audio ?? {
            play: (_key) => { },
            stop: (_key) => { },
            setVolume: (_category, _volume) => { },
        };
        this._openLegalDoc =
            opts.openLegalDoc ??
                ((doc) => {
                    if (typeof window !== 'undefined') {
                        window.open(`/legal/${doc}.html`, '_blank', 'noopener');
                    }
                });
        this._openShop = opts.openShop ?? (() => { });
        this._getLevelBackground = opts.getLevelBackground ?? (() => null);
        this._getLevelMusic = opts.getLevelMusic ?? (() => null);
        // Copied, not aliased: the caller's object stays theirs to keep, and a
        // later mutation of it cannot retroactively grant a game a capability the
        // host decided against at composition time.
        this._host = Object.freeze({ ...DEFAULT_GAME_HOST, ...opts.host });
    }
    /**
     * `onExitRequested` (WP-VIS.11) — the caller's graceful-exit sequence (fade-out
     * + awaited Pixi teardown + navigate). Falls back to a plain
     * `window.history.back()` when not supplied, preserving prior behavior for
     * any caller that hasn't been updated to pass it.
     */
    build(gameId, onExitRequested) {
        const provider = this._analyticsProvider;
        return {
            economy: this._economy,
            analytics: {
                logEvent: (event, params) => {
                    provider.logEvent(event, params ?? {});
                },
            },
            remoteConfig: this._remoteConfig,
            audio: this._audio,
            save: {
                getModule: (key) => this._saveModuleFactory(key),
            },
            localization: {
                t: (key) => key,
            },
            clock: new SystemClock(),
            requestExit: onExitRequested ??
                (() => {
                    if (typeof window !== 'undefined')
                        window.history.back();
                }),
            openLegalDoc: (doc) => this._openLegalDoc(doc, gameId),
            openShop: () => this._openShop(gameId),
            getLevelBackground: this._getLevelBackground,
            getLevelMusic: this._getLevelMusic,
            host: this._host,
        };
    }
}
